if (!customElements.get('jersey-customizer')) {
  customElements.define(
    'jersey-customizer',
    class JerseyCustomizer extends HTMLElement {
      constructor() {
        super();
        this.details = this.querySelector('details');
        this.tabButtons = Array.from(this.querySelectorAll('[data-jersey-tab]'));
        this.panels = {
          name: this.querySelector('[data-jersey-panel="name"]'),
          player: this.querySelector('[data-jersey-panel="player"]'),
        };
        this.nameInput = this.querySelector('[data-jersey-name]');
        this.numberInput = this.querySelector('[data-jersey-number]');
        this.playerSelect = this.querySelector('[data-jersey-player-select]');
        this.playerTabButton = this.querySelector('[data-jersey-tab="player"]');
        this.summary = this.querySelector('[data-jersey-summary]');
        this.summaryRow = this.querySelector('[data-jersey-summary-row]');
        this.clearButton = this.querySelector('[data-jersey-clear]');
        this.productFormId = this.dataset.productFormId;
        this.customizeOptionNameRaw = this.dataset.customizeOptionName || '';
        this.customizeOptionName = this.customizeOptionNameRaw.toLowerCase();

        this.unsubscribeOptionChange = undefined;
        this.submitGuard = this.submitGuard.bind(this);
      }

      connectedCallback() {
        this.tabButtons.forEach((button) => {
          button.addEventListener('click', () => this.switchTab(button.dataset.jerseyTab));
        });

        if (this.playerSelect) {
          this.playerSelect.addEventListener('change', () => this.applyPlayerSelection());
        }

        [this.nameInput, this.numberInput].forEach((input) => {
          if (!input) return;
          input.addEventListener('input', () => this.updateSummary());
        });

        if (this.nameInput) {
          this.nameInput.addEventListener('input', () => {
            const cursor = this.nameInput.selectionStart;
            this.nameInput.value = this.nameInput.value.toUpperCase();
            this.nameInput.setSelectionRange(cursor, cursor);
          });
        }

        if (this.clearButton) {
          this.clearButton.addEventListener('click', () => this.clearAndClose());
        }

        if (this.details) {
          this.details.addEventListener('toggle', () => this.updateSummary());
        }

        this.setupPlayerTabVisibility();
        this.updateSummary();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeOptionChange = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleOptionValueChange.bind(this)
          );
        }

        if (this.productFormId) {
          document.addEventListener('submit', this.submitGuard, true);
        }
      }

      disconnectedCallback() {
        this.unsubscribeOptionChange?.();
        document.removeEventListener('submit', this.submitGuard, true);
      }

      switchTab(tab) {
        this.tabButtons.forEach((button) => {
          const active = button.dataset.jerseyTab === tab;
          button.classList.toggle('jersey-tab--active', active);
        });
        Object.entries(this.panels).forEach(([name, panel]) => {
          if (panel) panel.hidden = name !== tab;
        });
      }

      applyPlayerSelection() {
        const value = this.playerSelect.value;
        if (!value) return;
        const [surname, number] = value.split(' - ');
        if (this.nameInput) this.nameInput.value = (surname || '').trim().toUpperCase();
        if (this.numberInput) this.numberInput.value = (number || '').trim();
        this.updateSummary();
      }

      updateSummary() {
        const name = this.nameInput?.value.trim() || '';
        const number = this.numberInput?.value.trim() || '';

        this.syncCustomizeVariant(!!(name || number));

        if (!this.summary || !this.summaryRow) return;

        if (!name && !number) {
          this.summaryRow.hidden = true;
          return;
        }

        const separator = name && number ? ' · #' : number ? '#' : '';
        this.summary.textContent = `${name}${separator}${number}`;
        this.summaryRow.hidden = false;
      }

      syncCustomizeVariant(shouldEnable) {
        if (!this.customizeOptionNameRaw) return;
        const root = this.closest('product-info') || document;
        const targetValue = shouldEnable ? 'Yes' : 'No';
        const radio = root.querySelector(
          `input[data-option-name="${this.customizeOptionNameRaw}"][value="${targetValue}"]`
        );
        if (radio && !radio.checked && !radio.disabled) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      clearAndClose() {
        if (this.nameInput) this.nameInput.value = '';
        if (this.numberInput) this.numberInput.value = '';
        if (this.playerSelect) this.playerSelect.value = '';
        this.details?.removeAttribute('open');
        this.updateSummary();
      }

      setupPlayerTabVisibility() {
        if (!this.playerTabButton || !this.playerSelect) return;

        const db = window.jerseyPlayerDB || {};
        const title = (this.dataset.productTitle || '').toLowerCase();
        const matchedKey = Object.keys(db).find((key) => title.includes(key));

        if (!matchedKey) {
          this.playerTabButton.hidden = true;
          this.switchTab('name');
          return;
        }

        const placeholder = this.playerSelect.querySelector('option[value=""]');
        this.playerSelect.innerHTML = '';
        if (placeholder) this.playerSelect.appendChild(placeholder);

        db[matchedKey].forEach((entry) => {
          const option = document.createElement('option');
          option.value = entry;
          option.textContent = entry;
          this.playerSelect.appendChild(option);
        });
      }

      handleOptionValueChange({ data: { target } }) {
        if (!target?.dataset?.optionName) return;
        if (!this.customizeOptionName) return;
        if (target.dataset.optionName.toLowerCase() !== this.customizeOptionName) return;

        const requiresCustomization = target.value.trim().toLowerCase() === 'yes';
        this.setRequired(requiresCustomization);
      }

      setRequired(required) {
        [this.nameInput, this.numberInput].forEach((input) => {
          if (!input) return;
          input.toggleAttribute('required', required);
          input.setAttribute('aria-required', required ? 'true' : 'false');
        });
      }

      submitGuard(event) {
        const form = event.target;
        if (!form || form.id !== this.productFormId) return;

        const invalidInput = [this.nameInput, this.numberInput].find(
          (input) => input && !input.checkValidity()
        );

        if (invalidInput) {
          event.preventDefault();
          event.stopImmediatePropagation();
          invalidInput.reportValidity();
          this.details?.setAttribute('open', 'open');
        }
      }
    }
  );
}
